// First-mount welcome card.
//
// Auto-shown the first time a trainee mounts a given panel kind (per
// localStorage flag in HelpProvider). "Got it" dismisses and writes
// the flag — never reappears on this device unless explicitly
// re-opened from the help menu.

import { useEffect } from "react";
import { useHelp } from "./help-context";
import { getHelpData } from "./help-data";

export function WelcomeCard() {
  const { panelKind, welcomeVisible, dismissWelcome } = useHelp();
  const data = getHelpData(panelKind);

  useEffect(() => {
    if (!welcomeVisible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissWelcome();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [welcomeVisible, dismissWelcome]);

  if (!welcomeVisible || !data.welcome) return null;
  const w = data.welcome;

  return (
    <div
      role="dialog"
      aria-label={`About ${w.title}`}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(43, 44, 47, 0.42)",
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) dismissWelcome();
      }}
    >
      <div
        style={{
          background: "var(--cream, #fbf3e7)",
          border: "1px solid var(--rule, #d8d3c7)",
          borderRadius: 12,
          padding: 28,
          maxWidth: 760,
          width: "92%",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 12px 40px rgba(27, 22, 10, 0.2)",
        }}
      >
        {w.eyebrow && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--orange-deep, #b8651b)",
              marginBottom: 6,
            }}
          >
            {w.eyebrow}
          </div>
        )}
        <h3
          style={{
            margin: "0 0 16px",
            fontSize: 22,
            fontWeight: 700,
            color: "var(--ink, #1b160a)",
          }}
        >
          {w.title}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 24,
            marginBottom: 20,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.55,
              color: "var(--ink, #1b160a)",
            }}
          >
            {w.body}
          </p>
          {w.highlights && w.highlights.length > 0 && (
            <ul
              style={{
                margin: 0,
                padding: 0,
                paddingLeft: 18,
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--body, #2b2c2f)",
              }}
            >
              {w.highlights.map((h, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {h}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={dismissWelcome}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "1px solid var(--ink, #1b160a)",
              background: "var(--ink, #1b160a)",
              color: "var(--white, #ffffff)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
